import logger from '~/services/logger';
import { BrightcoveEvents } from './types';
import type {
  BrightcoveEventData,
  BrightcoveAnalyticsConfig,
  SessionData,
} from './types';

export class BrightcoveDataCollector {
  private config: BrightcoveAnalyticsConfig;
  private sessionData: SessionData;
  private baseUrl: string;
  private engagementTimer?: NodeJS.Timeout;
  private playRequestSent: boolean = false;
  private videoViewSent: boolean = false;
  private isDisposed: boolean = false;

  constructor(config: BrightcoveAnalyticsConfig) {
    this.config = config;
    this.baseUrl =
      config.baseUrl || 'https://metrics.brightcove.com/v2/tracker';
    this.sessionData = {
      sessionId: config.sessionId || this.generateSessionId(),
      playbackStarted: false,
      lastEngagementTime: 0,
      lastVideoPosition: 0,
      totalWatched: 0,
      ranges: [],
    };
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private getBaseEventData(): Partial<BrightcoveEventData> {
    return {
      session: this.sessionData.sessionId,
      domain: 'videocloud', // Always required to be "videocloud"
      time: Date.now(), // Timestamp in epoch milliseconds
      account: this.config.accountId,
      player: this.config.playerId,
      player_name: this.config.playerName,
      user: this.config.userId,
      destination:
        typeof window !== 'undefined' ? window.location.href : undefined,
      source:
        typeof window !== 'undefined' && document.referrer
          ? document.referrer
          : undefined,
      device_type: this.getDeviceType(),
      device_os: this.getDeviceOS(),
      device_os_version: this.getDeviceOSVersion(),
      browser_type: this.getBrowserType(),
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
  }

  private getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|phone/i.test(userAgent)) {
      return 'mobile';
    }
    if (/tablet|ipad/i.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';
  }

  private getDeviceOS(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('windows')) return 'Windows';
    if (userAgent.includes('mac')) return 'macOS';
    if (userAgent.includes('linux')) return 'Linux';
    if (userAgent.includes('android')) return 'Android';
    if (
      userAgent.includes('ios') ||
      userAgent.includes('iphone') ||
      userAgent.includes('ipad')
    )
      return 'iOS';
    return 'Unknown';
  }

  private getDeviceOSVersion(): string | undefined {
    const userAgent = navigator.userAgent;
    const osVersionMatch = userAgent.match(
      /(?:Windows NT|Mac OS X|Android|iOS)\s*([0-9\._]+)/
    );
    return osVersionMatch ? osVersionMatch[1] : undefined;
  }

  private getBrowserType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('firefox')) return 'Firefox';
    if (userAgent.includes('chrome')) return 'Chrome';
    if (userAgent.includes('safari') && !userAgent.includes('chrome'))
      return 'Safari';
    if (userAgent.includes('edge')) return 'Edge';
    if (userAgent.includes('opera')) return 'Opera';
    return 'Unknown';
  }

  public async sendEvent(
    event: BrightcoveEvents,
    additionalData: Partial<BrightcoveEventData> = {}
  ): Promise<void> {
    if (this.isDisposed) {
      logger.info(
        'BrightcoveDataCollector: Attempted to send event after disposal'
      );
      return;
    }

    const eventData: BrightcoveEventData = {
      ...this.getBaseEventData(),
      event,
      ...additionalData,
    };

    try {
      // Convert to query parameters
      const params = new URLSearchParams();
      Object.entries(eventData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });

      const response = await fetch(`${this.baseUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'User-Agent': navigator.userAgent,
        },
      });

      if (response.ok) {
        logger.info(
          `BrightcoveDataCollector: Successfully sent ${event} event`
        );
      } else {
        logger.error(
          `BrightcoveDataCollector: Failed to send ${event} event. Status: ${response.status}`
        );
      }
    } catch (error) {
      logger.error(
        `BrightcoveDataCollector: Error sending ${event} event: ${error}`
      );
    }
  }

  public async trackPlayerLoad(): Promise<void> {
    await this.sendEvent(BrightcoveEvents.player_load);
  }

  public async trackVideoImpression(videoData: {
    id: string;
    name: string;
    duration: number;
  }): Promise<void> {
    await this.sendEvent(BrightcoveEvents.video_impression, {
      video: videoData.id,
      video_name: videoData.name,
      video_duration: videoData.duration,
    });
  }

  public async trackPlayRequest(videoData: {
    id: string;
    name: string;
    duration: number;
  }): Promise<void> {
    if (this.playRequestSent) return;

    await this.sendEvent(BrightcoveEvents.play_request, {
      video: videoData.id,
      video_name: videoData.name,
      video_duration: videoData.duration,
    });

    this.playRequestSent = true;
  }

  public async trackVideoView(
    videoData: { id: string; name: string; duration: number },
    loadTimeMs?: number,
    startTimeMs?: number
  ): Promise<void> {
    if (this.videoViewSent) return;

    await this.sendEvent(BrightcoveEvents.video_view, {
      video: videoData.id,
      video_name: videoData.name,
      video_duration: videoData.duration,
      load_time_ms: loadTimeMs,
      start_time_ms: startTimeMs,
    });

    this.videoViewSent = true;
    this.sessionData.playbackStarted = true;
  }

  public async trackVideoEngagement(
    currentTime: number,
    videoData: { id: string; name: string; duration: number }
  ): Promise<void> {
    if (!this.sessionData.playbackStarted) return;

    if (
      typeof currentTime !== 'number' ||
      isNaN(currentTime) ||
      currentTime < 0
    ) {
      return;
    }

    if (this.sessionData.lastVideoPosition === 0) {
      this.sessionData.lastVideoPosition = Math.floor(currentTime);
      this.sessionData.lastEngagementTime = Date.now() / 1000;
      return;
    }

    const now = Date.now() / 1000;
    const timeSinceLastUpdate = now - this.sessionData.lastEngagementTime;
    const currentVideoPosition = Math.floor(currentTime);
    const lastVideoPosition = this.sessionData.lastVideoPosition;

    if (timeSinceLastUpdate > 0 && currentVideoPosition > lastVideoPosition) {
      const rangeStart = lastVideoPosition;
      const rangeEnd = Math.min(
        currentVideoPosition,
        rangeStart + (this.config.engagementUpdateInterval || 20)
      );

      const watchedSeconds = rangeEnd - rangeStart;

      const maxRangeSeconds = this.config.engagementUpdateInterval || 20;
      const actualRangeEnd = Math.min(rangeEnd, rangeStart + maxRangeSeconds);

      const engagementData: Partial<BrightcoveEventData> = {
        video: videoData.id,
        video_name: videoData.name,
        video_duration: videoData.duration,
        range: `${rangeStart}..${actualRangeEnd}`,
        video_seconds_viewed: watchedSeconds,
      };

      // Add QoE data if enabled
      if (this.config.enableQoE && this.sessionData.currentRendition) {
        engagementData.rendition_url = this.sessionData.currentRendition.url;
        engagementData.rendition_indicated_bps =
          this.sessionData.currentRendition.bps;
        engagementData.rendition_mime_type =
          this.sessionData.currentRendition.mimeType;
        engagementData.rendition_width =
          this.sessionData.currentRendition.width;
        engagementData.rendition_height =
          this.sessionData.currentRendition.height;
      }

      if (this.sessionData.qoeData) {
        engagementData.rebuffering_seconds =
          this.sessionData.qoeData.rebufferingSeconds;
        engagementData.rebuffering_count =
          this.sessionData.qoeData.rebufferingCount;
        engagementData.dropped_frames = this.sessionData.qoeData.droppedFrames;

        // Reset QoE counters after sending
        this.sessionData.qoeData.rebufferingSeconds = 0;
        this.sessionData.qoeData.rebufferingCount = 0;
        this.sessionData.qoeData.droppedFrames = 0;
      }

      logger.info(
        `BrightcoveDataCollector: Sending engagement - Range: ${rangeStart}..${actualRangeEnd}, Watched: ${watchedSeconds}s`
      );

      await this.sendEvent(BrightcoveEvents.video_engagement, engagementData);

      // Update session data
      this.sessionData.lastVideoPosition = actualRangeEnd;
      this.sessionData.lastEngagementTime = now;
      this.sessionData.totalWatched += watchedSeconds;

      // Store range for internal tracking
      this.sessionData.ranges.push({
        start: rangeStart,
        end: actualRangeEnd,
        watchedSeconds: watchedSeconds,
      });
    }
  }

  public async trackError(
    errorCode: string,
    videoData?: { id: string; name: string; duration: number }
  ): Promise<void> {
    const errorData: Partial<BrightcoveEventData> = {
      error_code: errorCode,
    };

    if (videoData) {
      errorData.video = videoData.id;
      errorData.video_name = videoData.name;
      errorData.video_duration = videoData.duration;
    }

    await this.sendEvent(BrightcoveEvents.error, errorData);
  }

  public async trackAdModeBegin(): Promise<void> {
    await this.sendEvent(BrightcoveEvents.ad_mode_begin);
  }

  public async trackAdModeComplete(): Promise<void> {
    await this.sendEvent(BrightcoveEvents.ad_mode_complete);
  }

  public updateRenditionInfo(
    url: string,
    bps: number,
    mimeType: string,
    width: number,
    height: number
  ): void {
    this.sessionData.currentRendition = {
      url,
      bps,
      mimeType,
      width,
      height,
    };
  }

  public updateQoEData(
    rebufferingSeconds?: number,
    rebufferingCount?: number,
    droppedFrames?: number
  ): void {
    if (!this.sessionData.qoeData) {
      this.sessionData.qoeData = {
        rebufferingSeconds: 0,
        rebufferingCount: 0,
        droppedFrames: 0,
      };
    }

    if (rebufferingSeconds !== undefined) {
      this.sessionData.qoeData.rebufferingSeconds += rebufferingSeconds;
    }
    if (rebufferingCount !== undefined) {
      this.sessionData.qoeData.rebufferingCount += rebufferingCount;
    }
    if (droppedFrames !== undefined) {
      this.sessionData.qoeData.droppedFrames += droppedFrames;
    }
  }

  public startEngagementTracking(
    getCurrentTime: () => number,
    videoData: { id: string; name: string; duration: number }
  ): void {
    if (this.engagementTimer) {
      clearInterval(this.engagementTimer);
    }

    const interval = (this.config.engagementUpdateInterval || 20) * 1000; // Default 20 seconds

    this.engagementTimer = setInterval(() => {
      const currentTime = getCurrentTime();
      this.trackVideoEngagement(currentTime, videoData);
    }, interval);
  }

  public stopEngagementTracking(): void {
    if (this.engagementTimer) {
      clearInterval(this.engagementTimer);
      this.engagementTimer = undefined;
    }
  }

  public dispose(): void {
    this.isDisposed = true;
    this.stopEngagementTracking();
  }
}
